import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { modes } from '../content/home';
import { animateModes } from '../motion/controls';

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const motionIds = {
  desktop: [
    '4dfc4d34-55f5-28e1-ce79-4513bd143be3',
    '4dfc4d34-55f5-28e1-ce79-4513bd143bed',
    '4dfc4d34-55f5-28e1-ce79-4513bd143bf7',
  ],
  mobile: [
    '42bb7cec-73bc-a8c4-c4b4-4863420acb13',
    '42bb7cec-73bc-a8c4-c4b4-4863420acb2e',
    '42bb7cec-73bc-a8c4-c4b4-4863420acb49',
  ],
};

function ModeTabs({ mobile = false }: { mobile?: boolean }) {
  const [selected, select] = useState(0);
  const [enhanced, setEnhanced] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const prefix = mobile ? 'mobile-mode' : 'mode';
  useBrowserLayoutEffect(() => {
    setEnhanced(true);
  }, []);
  useBrowserLayoutEffect(() => {
    if (!enhanced || !root.current) return;
    const timeline = animateModes(root.current, selected, mobile);
    return () => {
      timeline.kill();
    };
  }, [enhanced, selected, mobile]);

  return (
    <div
      ref={root}
      data-enhanced={enhanced ? 'true' : undefined}
      className={`tabs${mobile ? ' is-mobile' : ''}`}
    >
      {modes.map((mode, i) => (
        <Fragment key={mode.id}>
          <button
            type="button"
            id={`${prefix}-button-${i}`}
            aria-expanded={enhanced ? selected === i : undefined}
            aria-controls={`${prefix}-panel-${i}`}
            disabled={!enhanced}
            onClick={() => select(i)}
            data-motion-id={motionIds[mobile ? 'mobile' : 'desktop'][i]}
            className={`tabs_link w-inline-block${selected === i ? ' active' : ''}`}
          >
            <span className="div-block-4 hover" aria-hidden="true">
              <span className="number_badge hover">
                <span>{i + 1}</span>
              </span>
              <span className="tabs_heading hover">{mode.label}</span>
            </span>
            <span className="div-block-4">
              <span className="number_badge">
                <span data-word-reveal={!mobile ? 'true' : undefined}>{i + 1}</span>
              </span>
              <span data-word-reveal="true" className="tabs_heading">
                {mode.label}
              </span>
            </span>
          </button>
          <section
            id={`${prefix}-panel-${i}`}
            aria-labelledby={`${prefix}-button-${i}`}
            aria-hidden={enhanced ? selected !== i : undefined}
            className={`tabs_content${mobile ? ' is-mobile' : ''}${i === 0 ? ' active' : ''}`}
          >
            <div className="tab_wrap">
              <div className="tab_video-wrapper">
                <BackgroundVideo name={mode.video} className="tab_video" active={selected === i} />
              </div>
              <div className="tab_content">
                <div className="max-width-small">
                  <h3
                    data-word-reveal={i === 0 || mobile ? 'true' : undefined}
                    className="text-size-xlarge"
                  >
                    {mode.title}
                  </h3>
                </div>
                <div className="max-width-small">
                  <p
                    data-word-reveal={i === 0 || mobile ? 'true' : undefined}
                    className="text-size-small"
                  >
                    {mode.paragraphs.map((paragraph, index) => (
                      <Fragment key={paragraph}>
                        {index > 0 && (
                          <>
                            <br />
                            <br />
                          </>
                        )}
                        {paragraph}
                      </Fragment>
                    ))}
                  </p>
                </div>
              </div>
            </div>
          </section>
        </Fragment>
      ))}
    </div>
  );
}

export function Modes() {
  return (
    <section id="modes" className="section_modes">
      <div className="padding-top padding-huge">
        <div className="content_modes">
          <div
            id="w-node-_490fc185-ed71-4232-ad92-9a9a8ffa0569-d6538631"
            className="container-full"
          >
            <div className="content_modes-top">
              <div className="header_divider-wrapper">
                <div data-motion-id="718e2422-b200-a35c-3893-ca7b7b5fd452" className="divider" />
                <div className="badge">
                  <div
                    data-motion-id="d9d3840a-d109-46ab-6f59-dfafccf2cb26"
                    className="badge-dot"
                  />
                  <div data-word-reveal="true">Autonomy research</div>
                </div>
              </div>
              <div className="heading_modes-grid">
                <h2 className="split-heading" aria-label="Three research perspectives">
                  <span data-word-reveal="true" className="heading-inline split-heading_text">
                    three
                  </span>{' '}
                  <span
                    data-motion-id="d92e6e4b-4e94-c9b3-9532-3eb79afe3733"
                    className="platform_number-wrapper"
                  >
                    <span data-word-reveal="true" className="heading_number split-heading_text">
                      3
                    </span>
                  </span>{' '}
                  <span data-word-reveal="true" className="heading-inline split-heading_text">
                    research perspectives
                  </span>
                </h2>
                <div id="w-node-_01d95efa-b7c4-d733-debe-3162d6931c3a-d6538631" className="note">
                  <img
                    src="/assets/images/Shape-81.svg"
                    loading="lazy"
                    data-motion-id="f45ed141-6428-9764-8468-a23b310cb164"
                    alt=""
                    className="icon-1x1-small"
                  />
                  <div data-word-reveal="true" className="text-size-small">
                    Observation. Navigation. Human oversight.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="content_modes-bottom">
            <ModeTabs />
            <ModeTabs mobile />
          </div>
        </div>
      </div>
    </section>
  );
}
